import { React, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Form, Table, Image, Modal, Button, Spinner } from 'react-bootstrap';
import * as api from 'services/api';
import _ from 'lodash';
import './UserList.css';
import { useTranslation } from 'react-i18next';
import { useUserContext } from 'contexts/UserContext';
import { fetchConfig } from '_helpers/fetch-helpers';

export function UsersList() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [userIdToDelete, setUserIdToDelete] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { userData } = useUserContext();
    const [roleHierarchy, setRoleHierarchy] = useState([]);

    useEffect(() => {
        getUsers();
        fetchConfig()
            .then(config => setRoleHierarchy(config?.roleHierarchy || []))
            .catch(() => setRoleHierarchy(['Admin', 'UserManager', 'User']));
    }, []);

    const getUsers = async () => {
        const response = await api.getUsers();

        if (response.status === 200) {
            setUsers(response.data);
        } else {
            console.error('Error al obtener el listado de usuarios');
        }
    };

    const handleNewUser = () => {
        navigate('/users/newUser');
    };

    const canManageUser = (targetRole) => {
        const myRoleIdx = roleHierarchy.indexOf(userData.role);
        const targetRoleIdx = roleHierarchy.indexOf(targetRole);
        return myRoleIdx !== -1 && targetRoleIdx !== -1 && myRoleIdx < targetRoleIdx;
    };

    const handleEditUser = (personId) => {
        const userToEdit = users.find(user => user.personId === personId);

        if (!userToEdit || !canManageUser(userToEdit.role)) {
            setShowAlert(true);
            return;
        }

        navigate(`/user/editUser/${personId}`);
    };

    const handleDeleteUser = (personId) => {
        const userToDelete = users.find(user => user.personId === personId);
        //setUserToDelete(userToDelete);

        if (!userToDelete || !canManageUser(userToDelete.role)) {
            setShowAlert(true);
            return;
        }

        setUserIdToDelete(personId);
        setShowModal(true);
    };

    const deleteUser = async () => {
        if (userIdToDelete) {
            const response = await api.deleteUser(userIdToDelete);

            if (response.status === 200) {
                setShowModal(false);
                setUserIdToDelete(null);
                getUsers();
            } else {
                console.error('Error al eliminar el usuario');
            }
        }
    };

    // Realizar la búsqueda en la tabla cada segundo 
    // después de que el usuario deje de escribir (debounce)
    const handleSearch = _.debounce(async (event) => {
        setLoading(true);
        const searchText = event.target.value;

        await new Promise((resolve) => setTimeout(resolve, 300));
        const response = await api.getUsers(searchText);

        setLoading(false);

        if (response.status === 200) {
            setUsers(response.data);
        } else {
            setError(t('usNotFoundResults'));
        }
    }, 1000);

    // Función para formatear la fecha de última conexión
    const formatLastConnection = (lastConnection) => {
        if (lastConnection !== null) {
            const date = new Date(lastConnection);
            const day = date.getDate();
            const month = date.getMonth() + 1;
            const year = date.getFullYear();
            const hours = date.getHours();
            const minutes = date.getMinutes();

            const formattedDay = day < 10 ? `0${day}` : day;
            const formattedMonth = month < 10 ? `0${month}` : month;
            const formattedHours = hours < 10 ? `0${hours}` : hours;
            const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

            return `${formattedDay}/${formattedMonth}/${year} ${formattedHours}:${formattedMinutes}`;
        }
    };

    return (
        <div className="UsersList">
            <Form>
                <Stack direction="horizontal" gap="3" className="mt-4 justify-content-between">
                    <div className="UsersList__searcherContainer">
                        <Form.Control
                            type="text"
                            placeholder={t('usNameUsername')}
                            className="UsersList__searcher bg-light"
                            onChange={handleSearch}
                        />
                        {loading && (
                            <Spinner animation="border" variant="secondary" size="sm" className="UsersList__spinner" />
                        )}
                    </div>
                    <Button type="button" variant="dark" onClick={handleNewUser}>{t('usNew')}</Button>
                </Stack>
                <Stack direction="horizontal" className="justify-content-start">
                    {error && <p className="text-danger fw-semibold">{error}</p>}
                </Stack>
            </Form>

            {users.length > 0 && (
                <div className="card rounded-3 overflow-auto p-2 mt-5">
                    <table className='table table-hover'>
                        <thead>
                            <tr>
                                <th >{t('usId')}</th>
                                <th >{t('usUsername')}</th>
                                <th >{t('usPhone')}</th>
                                <th >{t('usCentre')}</th>
                                <th >{t('usRole')}</th>
                                <th >{t('usLastConnection')}</th>
                                <th ></th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.personId}>
                                    <td className="align-middle">{user.personId}</td>
                                    <td className="align-middle" style={{ color: user.isActive ? 'black' : 'red' }}>
                                        <div><b>{user.username}</b></div>
                                        <div>{user.name} {user.surnames}</div>
                                    </td>
                                    <td className="align-middle">
                                        <div><b>{user.phone}</b></div>
                                    </td>
                                    <td className="align-middle" style={{ color: user.isActive ? 'black' : 'red' }}>
                                        <Image src="./icons/location.svg" className="UsersList__image" />
                                        {user.controlCentreText ? user.controlCentreText : <span style={{ marginLeft: '20px' }}>-</span>}
                                    </td>
                                    <td className="align-middle" style={{ color: user.isActive ? 'black' : 'red' }}>
                                        <Image src="./icons/user.svg" className="UsersList__image" />
                                        {user.role}
                                    </td>
                                    <td className="align-middle">
                                        <Image src="./icons/calendar.svg" className="UsersList__image" />
                                        {formatLastConnection(user.lastConnection) ? formatLastConnection(user.lastConnection) : <span style={{ marginLeft: '45px' }}>-</span>}
                                    </td>
                                    <td className="align-middle">
                                        <Stack direction="horizontal" gap="3" className="justify-content-center">
                                            <Image src="./icons/edit.svg" className="UsersList__icons" onClick={() => handleEditUser(user.personId)} />
                                            <Image src="./icons/delete.svg" className="UsersList__icons" onClick={() => handleDeleteUser(user.personId)} />
                                        </Stack>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {/* Modal para informar de que no se puedo borrar administradores */}
            {showAlert && (
                <Modal show={setShowAlert} onHide={() => setShowAlert(false)}>
                    <Modal.Header closeButton>
                        <Modal.Body>{t('usCannotEditAdmin')}</Modal.Body>
                    </Modal.Header>
                </Modal>
            )}

            {/* Modal para confirmar la eliminación del usuario */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('usConfirmDeletion')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{t('usQuestionDeletion')}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        {t('usCancel')}
                    </Button>
                    <Button variant="danger" onClick={deleteUser}>
                        {t('usAccept')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}